import { sendGTMEvent } from "@next/third-parties/google";
import { GtmEvents } from "@yardzen/next-client-util";

type EventParams = Record<string, unknown>;

/**
 * Every key that any tracked YFP event may attach to the dataLayer.
 * Each event sets only the keys it cares about; the rest are reset to
 * `undefined` so stale values from a prior push don't leak across events.
 */
const RESET_PARAMS: EventParams = {
  project_id: undefined,
  style_id: undefined,
  style_name: undefined,
  generated_image_url: undefined,
  error_message: undefined,
  product_name: undefined,
  vendor: undefined,
  category: undefined,
  estimate_id: undefined,
  total_cents: undefined,
  line_item_count: undefined,
  elements_count: undefined,
  materials_count: undefined,
  plants_count: undefined,
  element_vendors: undefined,
  material_vendors: undefined,
};

function dispatch(event: string, params: EventParams = {}): void {
  sendGTMEvent({ ...RESET_PARAMS, event, ...params });
}

export function trackLogin(): void {
  dispatch(GtmEvents.YFP_LOGIN);
}

export function trackSignupStart(): void {
  dispatch(GtmEvents.YFP_SIGNUP_START);
}

export function trackSignupComplete(): void {
  dispatch(GtmEvents.YFP_SIGNUP_COMPLETE);
}

export function trackYfpStart(): void {
  dispatch(GtmEvents.YFP_START);
}

export function trackPhotoUpload(): void {
  dispatch(GtmEvents.YFP_PHOTO_UPLOAD);
}

export function trackFrontYardSelect(): void {
  dispatch(GtmEvents.YFP_FRONT_YARD_SELECT);
}

export function trackBackYardSelect(): void {
  dispatch(GtmEvents.YFP_BACK_YARD_SELECT);
}

export function trackStyleSelect(styleId: string, styleName: string): void {
  dispatch(GtmEvents.YFP_STYLE_SELECT, {
    style_id: styleId,
    style_name: styleName,
  });
}

export interface CompleteEvent {
  projectId: string | null;
  generatedImageUrl: string;
  elementsCount: number;
  materialsCount: number;
  plantsCount: number;
  elementVendors: string[];
  materialVendors: string[];
}

export function trackComplete(data: CompleteEvent): void {
  dispatch(GtmEvents.YFP_COMPLETE, {
    project_id: data.projectId ?? undefined,
    generated_image_url: data.generatedImageUrl,
    elements_count: data.elementsCount,
    materials_count: data.materialsCount,
    plants_count: data.plantsCount,
    element_vendors: data.elementVendors,
    material_vendors: data.materialVendors,
  });
}

export function trackGenerationError(errorMessage: string): void {
  dispatch(GtmEvents.YFP_GENERATION_ERROR, { error_message: errorMessage });
}

export function trackEditFeaturesPromptClick(): void {
  dispatch(GtmEvents.YFP_EDIT_FEATURES_PROMPT_CLICK);
}

export function trackPickAnotherStyleClick(): void {
  dispatch(GtmEvents.YFP_PICK_ANOTHER_STYLE_CLICK);
}

export function trackPromptAdjustmentsClick(): void {
  dispatch(GtmEvents.YFP_PROMPT_ADJUSTMENTS_CLICK);
}

export function trackDownloadClick(
  generatedImageUrl: string | null | undefined,
): void {
  dispatch(GtmEvents.YFP_DOWNLOAD_CLICK, {
    generated_image_url: generatedImageUrl ?? undefined,
  });
}

export function trackCopyLinkClick(
  generatedImageUrl: string | null | undefined,
): void {
  dispatch(GtmEvents.YFP_COPY_LINK_CLICK, {
    generated_image_url: generatedImageUrl ?? undefined,
  });
}

export function trackDesignProjectClick(projectId: string): void {
  dispatch(GtmEvents.YFP_DESIGN_PROJECT_CLICK, { project_id: projectId });
}

export function trackDesignProjectPurchased(projectId: string): void {
  dispatch(GtmEvents.YFP_DESIGN_PROJECT_PURCHASED, { project_id: projectId });
}

export function trackStartEstimateClick(projectId: string): void {
  dispatch(GtmEvents.YFP_START_ESTIMATE_CLICK, { project_id: projectId });
}

export interface EstimateAmountEvent {
  estimateId: string;
  totalCents: number;
}

export interface EstimateSendEvent extends EstimateAmountEvent {
  lineItemCount: number;
}

export function trackEstimateSendClient(data: EstimateSendEvent): void {
  dispatch(GtmEvents.YFP_ESTIMATE_SEND_CLIENT, {
    estimate_id: data.estimateId,
    total_cents: data.totalCents,
    line_item_count: data.lineItemCount,
  });
}

export function trackEstimateExportPdf(data: EstimateAmountEvent): void {
  dispatch(GtmEvents.YFP_ESTIMATE_EXPORT_PDF, {
    estimate_id: data.estimateId,
    total_cents: data.totalCents,
  });
}

export function trackEstimateExportJobber(data: EstimateAmountEvent): void {
  dispatch(GtmEvents.YFP_ESTIMATE_EXPORT_JOBBER, {
    estimate_id: data.estimateId,
    total_cents: data.totalCents,
  });
}

export function trackEstimateExportBuildertrend(
  data: EstimateAmountEvent,
): void {
  dispatch(GtmEvents.YFP_ESTIMATE_EXPORT_BUILDERTREND, {
    estimate_id: data.estimateId,
    total_cents: data.totalCents,
  });
}

export interface ProductClickEvent {
  productName: string;
  vendor: string;
  category: string;
}

export function trackProductLearnMoreClick(product: ProductClickEvent): void {
  dispatch(GtmEvents.YFP_PRODUCT_LEARN_MORE_CLICK, {
    product_name: product.productName,
    vendor: product.vendor,
    category: product.category,
  });
}
